<template>
  <Toast />
  <ConfirmPopup></ConfirmPopup>
  <div class="provider-view">
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <h1 class="title">Data Offer Policies</h1>
          <span class="catalog-badge">Connector Catalog</span>
        </div>
        <div style="margin-left: auto">
          <button class="btn-header btn-primary-asset" @click="createPolicy">
            <svg class="i" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Policy
          </button>
        </div>
      </div>
    </div>

    <div class="filter-bar">
      <div class="filter-label">Filter</div>
      <div class="filter-group">
        <button
          class="filter-btn"
          :class="{ active: sourceView === 'all' }"
          @click="switchSource('all')"
        >
          <i class="pi pi-layer-group"></i>
          All
        </button>
        <button
          class="filter-btn"
          :class="{ active: sourceView === 'store' }"
          @click="switchSource('store')"
        >
          <i class="pi pi-box"></i>
          Store
          <span class="filter-count">{{ storePolicies.length }}</span>
        </button>
        <button
          class="filter-btn"
          :class="{ active: sourceView === 'connector' }"
          @click="switchSource('connector')"
        >
          <i class="pi pi-database"></i>
          Connector
          <span class="filter-count">{{ policies.length }}</span>
        </button>
      </div>
    </div>

    <section class="assets-section">
      <div v-if="visiblePolicies.length" class="policies-grid">
        <div
          v-for="item in visiblePolicies"
          :key="item.key"
          class="policy-card"
        >
          <div class="policy-card-header">
            <div class="policy-badge">
              <i class="pi pi-shield" style="font-size: 1rem"></i>
            </div>
            <div class="policy-title-section">
              <h3 class="policy-id">{{ getPolicyTitle(item) }}</h3>
              <div class="policy-type">{{ getPolicyTypeLabel(item) }}</div>
            </div>
          </div>

          <div class="policy-card-body">
            <div class="policy-info-item">
              <div class="asset-meta">
                <span
                  v-if="item.store && item.connector"
                  class="source-tag source-tag--ok"
                >
                  Store
                </span>
                <span
                  v-if="item.store && item.connector"
                  class="source-tag source-tag--ok"
                >
                  Connector
                </span>
                <span
                  v-if="item.store && !item.connector"
                  class="source-tag source-tag--bad"
                >
                  Store
                </span>
                <span
                  v-if="item.connector && !item.store"
                  class="source-tag source-tag--ok"
                >
                  Connector
                </span>
                <!-- <span
                  v-if="item.connector && !item.store"
                  class="source-tag source-tag--bad"
                >
                  In Store
                </span> -->
              </div>
            </div>

            <div v-if="item.connector" class="policy-info-item">
              <span class="info-label">
                <i class="pi pi-calendar"></i>
                Created
              </span>
              <span class="info-value">{{
                getFormattedDate(item.connector.createdAt)
              }}</span>
            </div>
            <div v-else-if="item.store" class="policy-info-item">
              <span class="info-label">
                <i class="pi pi-calendar"></i>
                Created
              </span>
              <span class="info-value">{{
                getFormattedDate(item.store.createdAt)
              }}</span>
            </div>

            <div v-if="item.connector?.['@id']" class="policy-info-item">
              <span class="info-label">
                <i class="pi pi-hashtag"></i>
                Policy ID
              </span>
              <span class="info-value">{{
                truncateId(item.connector["@id"])
              }}</span>
            </div>
            <div v-else-if="item.store?.externalId" class="policy-info-item">
              <span class="info-label">
                <i class="pi pi-hashtag"></i>
                Policy ID
              </span>
              <span class="info-value">{{
                truncateId(item.store.externalId)
              }}</span>
            </div>

            <div
              v-if="item.connector && getPermissionCount(item.connector)"
              class="policy-info-item"
            >
              <span class="info-label">
                <i class="pi pi-check-circle"></i>
                Permissions
              </span>
              <Badge
                :value="getPermissionCount(item.connector)"
                severity="success"
              />
            </div>

            <div
              v-if="item.connector && getObligationCount(item.connector)"
              class="policy-info-item"
            >
              <span class="info-label">
                <i class="pi pi-exclamation-circle"></i>
                Obligations
              </span>
              <Badge
                :value="getObligationCount(item.connector)"
                severity="info"
              />
            </div>

            <div
              v-if="item.connector && getProhibitionCount(item.connector)"
              class="policy-info-item"
            >
              <span class="info-label">
                <i class="pi pi-times-circle"></i>
                Prohibitions
              </span>
              <Badge
                :value="getProhibitionCount(item.connector)"
                severity="danger"
              />
            </div>

            <div v-if="item.store?.description" class="policy-info-item">
              <span class="info-value">{{ item.store.description }}</span>
            </div>
          </div>

          <div class="policy-card-actions">
            <Button
              v-if="item.store"
              @click="viewPolicyDetails(item.store)"
              label="View Details"
              icon="pi pi-eye"
              class="btn btn-secondary"
              outlined
            />
            <button
              v-if="item.connector && !item.store"
              class="btn btn-primary"
              @click="pushToStore(item.connector)"
            >
              Push to Store
            </button>
            <button
              v-if="item.store"
              class="delete-icon"
              @click="confirm2($event, item.store)"
              aria-label="Delete asset"
            >
              <svg viewBox="0 0 24 24">
                <path
                  d="M3 6h18M8 6V4h8v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <div class="empty-icon">
          <i
            class="pi"
            :class="{
              'pi-box': sourceView === 'store',
              'pi-shield': sourceView === 'connector',
              'pi-layer-group': sourceView === 'all',
            }"
            style="font-size: 3rem; color: #a0aec0"
          ></i>
        </div>
        <h3 class="empty-title">{{ emptyTitle }}</h3>
        <p class="empty-description">{{ emptyDescription }}</p>
        <Button
          v-if="sourceView === 'connector'"
          label="Create First Policy"
          icon="pi pi-plus"
          class="btn btn-primary empty-action"
          @click="createPolicy"
        />
      </div>
    </section>

    <Dialog
      v-model:visible="policyEditorVisible"
      :style="{ width: '70vw', maxWidth: '1200px' }"
      :maximizable="true"
      :modal="true"
      :closable="true"
      :header="viewOnly ? 'Policy Details' : 'Basic Policy Editor'"
      class="policy-editor-dialog"
      :breakpoints="{
        '1199px': '95vw',
        '575px': '100vw',
      }"
    >
      <div v-if="!viewOnly" class="editor-mode-selector">
        <div class="mode-buttons">
          <button
            class="mode-btn"
            :class="{ 'mode-btn--active': editorMode === 'editor' }"
            @click="switchMode('editor')"
          >
            <i class="pi pi-code mode-icon"></i>
            <span>Code Editor</span>
          </button>
          <button
            class="mode-btn"
            :class="{ 'mode-btn--active': editorMode === 'form' }"
            @click="switchMode('form')"
          >
            <i class="pi pi-file-edit mode-icon"></i>
            <span>Form Editor</span>
          </button>
        </div>
      </div>

      <div v-if="editorMode === 'editor'" class="editor-mode">
        <div v-if="!viewOnly" class="instructions-panel">
          <div class="instructions-header">
            <i class="pi pi-info-circle instructions-icon"></i>
            <h3 class="instructions-title">How to Use the Policy Editor</h3>
            <i
              class="pi pi-eye instructions-icon"
              @click="toggleInstructions"
            ></i>
          </div>
          <div class="instructions-content" v-show="instructionsVisible">
            <div class="instruction-step">
              <div class="step-number">1</div>
              <div class="step-content">
                <strong>Start with the template</strong> - Modify the JSON
                structure below to define your policy rules.
              </div>
            </div>
            <div class="instruction-step">
              <div class="step-number">2</div>
              <div class="step-content">
                <strong>Policy Structure</strong> - Each policy must have:
                <ul>
                  <li><code>@id</code>: Unique identifier for your policy</li>
                  <li>
                    <code>policy.permission</code>: Access rules (or
                    obligation/prohibition)
                  </li>
                  <li><code>constraint</code>: Conditions for access</li>
                  <li>
                    <code>leftOperand</code>: Currenly only
                    <strong>AgreementAccess.dataspace</strong>,
                    <strong>DataAccess.limit</strong> and
                    <strong>MembershipCredential</strong> are supported
                  </li>
                </ul>
              </div>
            </div>
            <div class="instruction-step">
              <div class="step-number">3</div>
              <div class="step-content">
                <strong>Need help?</strong> click "Load Examples" for sample
                policies.
              </div>
            </div>
          </div>
        </div>

        <div class="code-editor-section">
          <div class="editor-header">
            <div class="header-left">
              <label class="section-label">
                <i class="pi pi-code"></i>
                Policy JSON Editor
              </label>
              <div class="language-badge">
                <i class="pi pi-brackets-curly"></i>
                <span>JSON</span>
              </div>
            </div>
            <div v-if="!viewOnly" class="header-right">
              <Button
                icon="pi pi-refresh"
                label="Reset"
                @click="resetEditor"
                size="small"
                outlined
                severity="secondary"
              />
              <Button
                icon="pi pi-download"
                label="Load Examples"
                @click="showExamples = true"
                size="small"
                outlined
              />
              <Button
                icon="pi pi-upload"
                label="Import JSON"
                @click="$refs.fileInput.click()"
                size="small"
                outlined
              />
              <input
                type="file"
                ref="fileInput"
                accept=".json"
                @change="handleFileImport"
                style="display: none"
              />
            </div>
          </div>

          <div
            class="code-editor-container"
            :class="{ 'has-error': jsonError }"
          >
            <div class="editor-toolbar">
              <div class="editor-info">
                <span class="line-count">{{ lineCount }} lines</span>
                <span class="char-count">{{ charCount }} characters</span>
              </div>
              <div class="editor-actions">
                <Button
                  icon="pi pi-copy"
                  @click="copyToClipboard"
                  text
                  rounded
                  v-tooltip="'Copy to clipboard'"
                />
                <Button
                  icon="pi pi-search"
                  v-if="!viewOnly"
                  @click="formatJSON"
                  text
                  rounded
                  v-tooltip="'Format JSON'"
                />
                <Button
                  icon="pi pi-check"
                  v-if="!viewOnly"
                  @click="validateJSON"
                  text
                  rounded
                  v-tooltip="'Validate JSON'"
                  :class="{ 'p-button-success': !jsonError }"
                />
              </div>
            </div>

            <Textarea
              v-model="policyJSON"
              :rows="17"
              :readonly="viewOnly"
              spellcheck="false"
              class="json-editor"
              @input="onEditorChange"
              placeholder="// Start typing your policy JSON here..."
              :class="{ 'editor-error': jsonError }"
            />

            <div v-if="jsonError" class="error-message">
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ jsonError }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="form-editor-mode">
        <div class="form-editor">
          <div class="simple-form">
            <div class="form-section">
              <h4 class="section-title">Basic Information</h4>
              <div class="form-row">
                <div class="form-field">
                  <label for="policyName"> Policy Name * </label>
                  <div class="input-container">
                    <input
                      id="policyName"
                      v-model="formData.name"
                      @input="generatePolicyId"
                      type="text"
                      placeholder="e.g., My Data Policy"
                      class="endpoint-input"
                    />
                  </div>
                  <small class="p-error" v-if="!formData.name"
                    >Policy Name is required</small
                  >
                </div>

                <div class="form-field">
                  <label for="policyType"> Policy Type * </label>
                  <Dropdown
                    id="policyType"
                    v-model="formData.type"
                    :options="policyTypes"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select policy type"
                    class="type-dropdown"
                  />
                </div>
              </div>
              <div class="form-field" style="margin-top: 10px">
                <label for="policyDescription"> Policy Description </label>
                <div class="input-container">
                  <textarea
                    id="policyDescription"
                    v-model="formData.description"
                    placeholder="Brief description of the policy"
                    class="endpoint-input"
                    rows="5"
                    cols="33"
                  ></textarea>
                </div>
              </div>
            </div>

            <div class="form-section">
              <div class="instructions-panel">
                <div class="instructions-header">
                  <i class="pi pi-info-circle instructions-icon"></i>
                  <div>
                    <strong>Need constraints or advanced policies?</strong>
                    <div>
                      Install PCR to explore and add policy constraints. The
                      basic editor creates an empty rule that you can complete
                      manually in the code editor.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        v-model:visible="showExamples"
        header="Policy Examples"
        :style="{ width: '50vw' }"
        :modal="true"
      >
        <div class="examples-grid">
          <div
            v-for="example in policyExamples"
            :key="example.id"
            class="example-card"
            @click="loadExample(example.id)"
          >
            <div class="example-header">
              <i :class="example.icon" class="example-icon"></i>
              <h5 class="example-title">{{ example.title }}</h5>
            </div>
            <p class="example-description">{{ example.description }}</p>
            <div class="example-tags">
              <Chip
                v-for="tag in example.tags"
                :key="tag"
                :label="tag"
                class="example-tag"
              />
            </div>
          </div>
        </div>
        <template #footer>
          <Button
            label="Close"
            @click="showExamples = false"
            class="p-button-text"
          />
        </template>
      </Dialog>

      <template #footer>
        <Button
          :label="viewOnly ? 'Close' : 'Cancel'"
          :icon="viewOnly ? 'pi pi-check' : 'pi pi-times'"
          @click="policyEditorVisible = false"
          class="p-button-text"
        />
        <Button
          v-if="!viewOnly"
          label="Code Editor"
          icon="pi pi-code"
          @click="switchToCodeEditor"
          severity="secondary"
          outlined
        />
        <Button
          v-if="!viewOnly"
          label="Save Policy"
          icon="pi pi-save"
          @click="savePolicyFromEditor"
          class="p-button-primary"
        />
        <!-- <Button
          v-if="editPolicy"
          label="Edit Policy"
          icon="pi pi-save"
          @click="EditPolicyFromEditor"
          class="p-button-primary"
        /> -->
      </template>
    </Dialog>
    <Dialog
      v-model:visible="pcrIframeVisible"
      header="Policy Details"
      :style="{ width: '100vw', height: '90vh' }"
      modal
    >
      <iframe
        v-if="pcrIframeUrl"
        :src="pcrIframeUrl"
        title="PCR Policy"
        style="width: 100%; height: 80vh; border: 0"
      ></iframe>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, watch } from "vue";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Badge from "primevue/badge";
import Textarea from "primevue/textarea";
import ConfirmPopup from "primevue/confirmpopup";
import { useConfirm } from "primevue/useconfirm";

import Dropdown from "primevue/dropdown";
import Chip from "primevue/chip";
import axios from "axios";
import { getCurrentInstance } from "vue";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";

const { appContext } = getCurrentInstance();
const API_BASE_URL = appContext.config.globalProperties.$apiBase;

const props = defineProps({
  policies: {
    type: Array,
    default: () => [],
  },
  storePolicies: {
    type: Array,
    default: () => [],
  },
  user: {
    type: String,
    default: null,
  },
  initialSource: {
    type: String,
    default: "all",
  },
  mergedPolicies: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["refresh-assets", "push-to-store"]);
const toast = useToast();

const sourceView = ref(props.initialSource || "all");
const policyEditorVisible = ref(false);
const selectedPolicy = ref(null);
const showPublishModal = ref(false);
const showPreview = ref(false);
const showExamples = ref(false);
const instructionsVisible = ref(false);
const confirm = useConfirm();

const editorMode = ref("editor");
const editPolicy = ref(false);
const policyObject = ref(null);
const policyJSON = ref("");
const jsonError = ref("");
const lineCount = ref(0);
const charCount = ref(0);
const policyAdvancedOpen = ref(false);
const policyExistsInConnector = ref(false);
const pcrIframeVisible = ref(false);
const pcrIframeUrl = ref("");
const viewOnly = ref(false);
const policyIdSuffix = ref("");

const formData = ref({
  id: "",
  name: "",
  description: "",
  type: "permission",
  action: "use",
});

const customPolicy = ref({
  id: "",
  action: "use",
  constraintType: "single",
  ruleType: "obligation",
  constraints: [
    {
      "@type": "Constraint",
      leftOperand: "",
      operator: {
        "@id": "",
      },
      rightOperand: {
        "@value": null,
        "@type": "xsd:dateTime",
      },
    },
  ],
});

const policyTypes = [
  { value: "permission", label: "Permission (Allow access)" },
  { value: "obligation", label: "Obligation (Require action)" },
  { value: "prohibition", label: "Prohibition (Deny access)" },
];

const policyExamples = [
  {
    id: "data-access",
    title: "Data Access Policy",
    description: "Basic policy for data access",
    icon: "pi pi-lock",
    tags: ["Basic", "Obligation"],
  },
  {
    id: "limit-based",
    title: "Limit Based Policy",
    description: "Sets a limit for the transfers",
    icon: "pi pi-clock",
    tags: ["Basic", "Obligation"],
  },
  {
    id: "membership-based",
    title: "Membership Credentials",
    description: "Checks the membership credentials",
    icon: "pi pi-filter",
    tags: ["Basic", "Permission"],
  },
];

const switchSource = (view) => {
  sourceView.value = view;
};

const totalPolicies = computed(
  () => props.storePolicies.length + props.policies.length
);

const getStoreId = (policy) =>
  policy?.externalId || policy?.contentData?.["@id"] || policy?.id;
const getStoreName = (policy) => policy?.name || policy?.contentData?.name;
const getConnectorId = (policy) => policy?.["@id"] || policy?.policy?.["@id"];
const getConnectorName = (policy) => policy?.name;

const storeById = computed(() => {
  const map = new Map();
  props.storePolicies.forEach((policy) => {
    const key = getStoreId(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const storeByName = computed(() => {
  const map = new Map();
  props.storePolicies.forEach((policy) => {
    const key = getStoreName(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const connectorById = computed(() => {
  const map = new Map();
  props.policies.forEach((policy) => {
    const key = getConnectorId(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const connectorByName = computed(() => {
  const map = new Map();
  props.policies.forEach((policy) => {
    const key = getConnectorName(policy);
    if (key) map.set(key, policy);
  });
  return map;
});

const findConnectorForStore = (policy) => {
  const id = getStoreId(policy);
  if (id && connectorById.value.has(id)) return connectorById.value.get(id);
  const name = getStoreName(policy);
  if (name && connectorByName.value.has(name))
    return connectorByName.value.get(name);
  return null;
};

const findStoreForConnector = (policy) => {
  const id = getConnectorId(policy);
  if (id && storeById.value.has(id)) return storeById.value.get(id);
  const name = getConnectorName(policy);
  if (name && storeByName.value.has(name)) return storeByName.value.get(name);
  return null;
};

const pushToStore = async (policy) => {
  const policyStorePayload = {
    ownerId: props.user,
    externalId: policy["@id"],
    objectType: { name: "DataOffer-Policy", level: 2 },
    name: policy.name || policy["@id"] || "Unnamed Policy",
    description: policy.description || "",
    comments: policy.comments || "",
    version: policy.version || "1.0",
    tags: policy.tags || [],
    metadata: {
      additionalProp1: {
        inConnector: true,
      },
    },
    contentType: "json",
    contentData: policy,
  };
  console.log("Pushing policy to store:", policyStorePayload);
  const response = await axios.post(
    `${API_BASE_URL}/repository/insert`,
    policyStorePayload,
    {
      params: {
        "API-KEY": "C5CC3F19",
      },
      headers: {
        Accept: "application/json",
        "API-KEY": "C5CC3F19",
      },
    }
  );

  console.log("Response from asset store: ", response.data);

  if (response.data.acknowledged) {
    // Reset form and notify parent
    emit("refresh-assets");
    //emit("update:modelValue", "subscriber");
  } else {
    throw new Error(response.data.error || "Failed to register asset");
  }
};

const storeViewPolicies = computed(() =>
  props.storePolicies.map((policy) => {
    const key = getStoreId(policy) || getStoreName(policy);
    return {
      key: key || policy.id,
      store: policy,
      connector: findConnectorForStore(policy),
    };
  })
);

const connectorViewPolicies = computed(() =>
  props.policies.map((policy) => {
    const key = getConnectorId(policy) || getConnectorName(policy);
    return {
      key: key || policy["@id"],
      store: findStoreForConnector(policy),
      connector: policy,
    };
  })
);

const visiblePolicies = computed(() => {
  if (sourceView.value === "all") return props.mergedPolicies;
  if (sourceView.value === "store") return storeViewPolicies.value;
  return connectorViewPolicies.value;
});

const getPolicyTitle = (item) =>
  item.store?.name ||
  item.connector?.name ||
  getPolicyId(
    item.connector?.["@id"] || item.connector?.policy?.["@id"] || ""
  ) ||
  "Unnamed policy";

const getPolicyTypeLabel = (item) => {
  if (item.store && !item.connector) return "Store Policy";
  if (item.connector && !item.store)
    return getPolicyType(item.connector["@type"]);
  if (item.connector) return getPolicyType(item.connector["@type"]);
  return "Store Policy";
};

const emptyTitle = computed(() => {
  if (sourceView.value === "store") return "No store policies yet";
  if (sourceView.value === "connector") return "No policies available";
  return "No policies available";
});

const emptyDescription = computed(() => {
  if (sourceView.value === "store") {
    return "Store policies will appear here once the store database is connected.";
  }
  if (sourceView.value === "connector") {
    return "Policies define access rules and constraints for your data assets.";
  }
  return "Once policies are added, they will appear here from both sources.";
});

const loadExample = (exampleId) => {
  let example = "";

  switch (exampleId) {
    case "data-access":
      example = `{
  "@context": ["https://w3id.org/edc/connector/management/v0.0.1"],
  "@type": "PolicyDefinition",
  "@id": "require-access",
  "name": "Data Access Policy",
  "description": "A policy that requires access to a specific data space.",
  "policy": {
    "@type": "Set",
    "obligation": [
      {
        "action": "use",
        "constraint": {
          "leftOperand": "AgreementAccess.dataspace",
          "operator": "eq",
          "rightOperand": "DataspaceA"
        }
      }
    ]
  }
}`;
      break;
    case "limit-based":
      example = `{
  "@context": ["https://w3id.org/edc/connector/management/v0.0.1"],
  "@type": "PolicyDefinition",
  "@id": "require-dataprocessor",
  "name": "Limit Based Policy",
  "description": "A policy that sets a limit on data processing.",
  "policy": {
    "@type": "Set",
    "obligation": [
      {
        "action": "use",
        "constraint": {
          "leftOperand": "DataAccess.limit",
          "operator": "eq",
          "rightOperand": "100"
        }
      }
    ]
  }
}`;
      break;
    case "membership-based":
      example = `{
  "@context": ["https://w3id.org/edc/connector/management/v0.0.1"],
  "@type": "PolicyDefinition",
  "@id": "require-membership",
  "name": "Membership Based Policy",
  "description": "A policy that checks the membership credentials.",
  "policy": {
    "@type": "Set",
    "permission": [
      {
        "action": "use",
        "constraint": {
          "leftOperand": "MembershipCredential",
          "operator": "eq",
          "rightOperand": "active"
        }
      }
    ]
  }
}`;
      break;
  }

  policyJSON.value = example;
  updateEditorStats();
  showExamples.value = false;
};

const formValid = computed(() => {
  return Boolean(
    formData.value.id && formData.value.name && formData.value.type
  );
});

const formJSONPreview = computed(() => {
  try {
    const policy = {
      "@context": ["https://w3id.org/edc/connector/management/v0.0.1"],
      "@type": "PolicyDefinition",
      "@id": formData.value.id || "untitled-policy",
      name: formData.value.name || "Untitled Policy",
      description: formData.value.description || "",
      policy: {
        "@type": "Set",
        [formData.value.type]: [],
      },
    };
    return JSON.stringify(policy, null, 2);
  } catch (error) {
    return JSON.stringify({ error: "Invalid policy configuration" }, null, 2);
  }
});

const generateCustomPolicy = computed(() => {
  const ruleKey = customPolicy.value.ruleType;
  const policy = {
    "@context": ["https://w3id.org/edc/connector/management/v0.0.1"],
    "@type": "PolicyDefinition",
    "@id": customPolicy.value.id,
    policy: {
      "@type": "Set",
      [ruleKey]: [
        {
          action: customPolicy.value.action,
          constraint:
            customPolicy.value.constraintType === "single"
              ? customPolicy.value.constraints[0]
              : {
                  [customPolicy.value.constraintType]:
                    customPolicy.value.constraints,
                },
        },
      ],
    },
  };
  return JSON.stringify(policy, null, 2);
});

const toggleInstructions = () => {
  instructionsVisible.value = !instructionsVisible.value;
};

const generatePolicyId = () => {
  const slug = formData.value.name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  formData.value.id = slug ? `${slug}-${policyIdSuffix.value}` : "";
};

const openPolicyEditor = () => {
  editorMode.value = "form";
  viewOnly.value = false;
  policyAdvancedOpen.value = false;
  policyExistsInConnector.value = false;
  editPolicy.value = false;
  policyIdSuffix.value = Math.floor(100000 + Math.random() * 900000).toString();
  formData.value = {
    id: "",
    name: "",
    description: "",
    type: "permission",
    action: "use",
  };
  policyJSON.value = formJSONPreview.value;
  updateEditorStats();

  policyEditorVisible.value = true;
};

const isPcrHealthy = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/pcr/health`, {
      timeout: 5000,
    });
    return response.data?.available === true;
  } catch (error) {
    console.info("PCR is unavailable; using the basic policy editor.", error);
    return false;
  }
};

const openPCR = async () => {
  const response = await axios.get(`${API_BASE_URL}/pcr`);
  console.log("PCR response: ", response.data);
  const pcrUrl = response.data.url;
  window.open(pcrUrl, "_blank");
};

const openReadOnlyPolicy = (policy) => {
  const content = policy?.contentData || policy;
  viewOnly.value = true;
  editPolicy.value = true;
  editorMode.value = "editor";
  policyJSON.value = JSON.stringify(content, null, 2);
  jsonError.value = "";
  updateEditorStats();
  policyEditorVisible.value = true;
};

const viewPolicyDetails = async (policy) => {
  if (await isPcrHealthy()) {
    const response = await axios.get(`${API_BASE_URL}/pcr/${policy.id}`);
    pcrIframeUrl.value = response.data.url;
    pcrIframeVisible.value = true;
    return;
  }

  openReadOnlyPolicy(policy);
};

const parsePolicyToForm = (policy) => {
  try {
    formData.value.id = policy["@id"] || "";
    formData.value.type = policy.policy?.permission
      ? "permission"
      : policy.policy?.obligation
      ? "obligation"
      : policy.policy?.prohibition
      ? "prohibition"
      : "permission";

    const rule =
      policy.policy?.permission?.[0] ||
      policy.policy?.obligation?.[0] ||
      policy.policy?.prohibition?.[0];

    if (rule) {
      formData.value.action = rule.action || "use";

      if (rule.constraint) {
        if (rule.constraint.and || rule.constraint.or) {
          const constraints =
            rule.constraint[rule.constraint.and ? "and" : "or"];
          if (constraints && constraints.length > 0) {
            formData.value.constraints = [constraints[0]];
          }
        } else {
          formData.value.constraints = [rule.constraint];
        }
      }
    }
  } catch (error) {
    console.error("Error parsing policy to form:", error);
  }
};

const switchMode = (mode) => {
  editorMode.value = mode;
  if (mode === "editor") {
    policyJSON.value = formJSONPreview.value;
    updateEditorStats();
  }
};

const switchToCodeEditor = () => {
  switchMode("editor");
};

const loadBasicTemplate = () => {
  const template = `{
  "@context": ["https://w3id.org/edc/connector/management/v0.0.1"],
  "@type": "PolicyDefinition",
  "@id": "my-basic-policy",
  "name": "My Basic Policy",
  "description": "A basic policy for demonstration purposes.",
  "policy": {
    "@type": "Set",
    "permission": [
      {
        "action": "use",
        "constraint": {
          "leftOperand": "DataAccess.level",
          "operator": "eq",
          "rightOperand": "standard"
        }
      }
    ]
  }
}`;

  policyJSON.value = template;
  updateEditorStats();
};

const resetEditor = () => {
  policyJSON.value = "";
  jsonError.value = "";
  updateEditorStats();
};

const handleFileImport = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const json = JSON.parse(e.target.result);
      policyJSON.value = JSON.stringify(json, null, 2);
      updateEditorStats();
    } catch (error) {
      console.error("Invalid JSON file:", error);
    }
  };
  reader.readAsText(file);
  event.target.value = "";
};

const onEditorChange = () => {
  updateEditorStats();
  jsonError.value = "";
};

const updateEditorStats = () => {
  lineCount.value = policyJSON.value.split("\n").length;
  charCount.value = policyJSON.value.length;
};

const formatJSON = () => {
  try {
    const parsed = JSON.parse(policyJSON.value);
    policyJSON.value = JSON.stringify(parsed, null, 2);
  } catch (error) {
    jsonError.value = "Invalid JSON: " + error.message;
  }
};

const validateJSON = () => {
  try {
    JSON.parse(policyJSON.value);
    jsonError.value = "";
  } catch (error) {
    jsonError.value = error.message;
  }
};

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(policyJSON.value);
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
  }
};

const copyFormJSON = async () => {
  try {
    await navigator.clipboard.writeText(formJSONPreview.value);
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
  }
};

const EditPolicyFromEditor = async () => {
  try {
    let policyPayload;

    if (editorMode.value === "editor") {
      try {
        policyPayload = JSON.parse(policyJSON.value);
      } catch (error) {
        console.error("Invalid JSON:", error);
        return;
      }
    } else {
      if (!formValid.value) {
        console.error("Form validation failed");
        return;
      }
      policyPayload = JSON.parse(formJSONPreview.value);
    }

    const policyStorePayload = {
      id: policyObject.value,
      ownerId: props.user || "unknown",
      externalId: policyPayload["@id"] || `policy-${Date.now()}`,
      objectType: { name: "policy", level: 0 },
      name: policyPayload.name || "Unnamed Policy",
      description: policyPayload.description || "",
      comments: policyPayload.comments || "",
      version: policyPayload.version || "1.0",
      tags: policyPayload.tags || [],
      metadata: selectedPolicy.value?.metadata,
      contentType: "json",
      contentData: policyPayload,
    };

    console.log("Selected policy for editing:", selectedPolicy.value);

    console.log("Payload to store in repository:", policyStorePayload);

    if (selectedPolicy.value?.metadata.additionalProp1.inConnector) {
      const policyResponse = await axios.post(
        `${API_BASE_URL}/provider/policy/edit/${policyStorePayload.externalId}`,
        {
          policy: policyStorePayload.contentData,
        }
      );

      console.log("Response: ", policyResponse);

      if (policyResponse.data.success) {
        await axios.post(
          `${API_BASE_URL}/repository/update/full`,
          policyStorePayload,
          {
            params: {
              "API-KEY": "C5CC3F19",
            },
            headers: {
              Accept: "application/json",
              "API-KEY": "C5CC3F19",
            },
          }
        );
        policyEditorVisible.value = false;

        formData.value = {
          id: "",
          type: "permission",
          action: "use",
          constraints: [{ leftOperand: "", operator: "eq", rightOperand: "" }],
        };

        policyJSON.value = "";
        toast.add({
          severity: "success",
          summary: "Policy Edited",
          life: 3000,
        });
        emit("refresh-assets");
      } else {
        throw new Error(policyResponse.data.error || "Failed to save policy");
      }
    } else {
      const response = await axios.post(
        `${API_BASE_URL}/repository/update/full`,
        policyStorePayload,
        {
          params: {
            "API-KEY": "C5CC3F19",
          },
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.data.acknowledged) {
        policyEditorVisible.value = false;

        formData.value = {
          id: "",
          type: "permission",
          action: "use",
          constraints: [{ leftOperand: "", operator: "eq", rightOperand: "" }],
        };

        policyJSON.value = "";
        emit("refresh-assets");
      } else {
        throw new Error(response.data.error || "Failed to save policy");
      }
    }
  } catch (error) {
    console.log("Error message: ", error.response.data?.detail);

    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: error.response.data?.detail,
      life: 5000,
    });
  }
};

const savePolicyFromEditor = async () => {
  try {
    let policyPayload;

    if (editorMode.value === "editor") {
      try {
        policyPayload = JSON.parse(policyJSON.value);
      } catch (error) {
        console.error("Invalid JSON:", error);
        return;
      }
    } else {
      if (!formValid.value) {
        throw new Error("Complete the policy name and type fields");
      }

      policyPayload = {
        "@context": ["https://w3id.org/edc/connector/management/v0.0.1"],
        "@type": "PolicyDefinition",
        "@id": formData.value.id,
        name: formData.value.name,
        description: formData.value.description || "",
        policy: {
          "@type": "Set",
          [formData.value.type]: [],
        },
      };

      console.log("Policy payload from form:", policyPayload);
    }

    const policyStorePayload = {
      ownerId: props.user || "unknown",
      externalId: policyPayload["@id"] || `policy-${Date.now()}`,
      objectType: { name: "DataOffer-Policy", level: 0 },
      name: policyPayload.name || "Unnamed Policy",
      description: policyPayload.description || "",
      comments: policyPayload.comments || "",
      version: policyPayload.version || "1.0",
      tags: policyPayload.tags || [],
      metadata: {
        additionalProp1: {
          inConnector: Boolean(policyExistsInConnector.value),
        },
      },
      contentType: "json",
      contentData: policyPayload,
    };

    console.log("Payload to store in repository:", policyStorePayload);
    console.log("Policy Store: ", props.storePolicies);

    const exist = props.storePolicies.some((policy) => {
      return (
        policy.externalId == policyStorePayload.externalId ||
        policy.name == policyStorePayload.name
      );
    });

    if (exist) {
      throw new Error("Policy already exists");
    }

    console.log(exist);

    const response = await axios.post(
      `${API_BASE_URL}/repository/insert`,
      policyStorePayload,
      {
        params: {
          "API-KEY": "C5CC3F19",
        },
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Response from repository API:", response.data);

    if (response.data.acknowledged) {
      policyEditorVisible.value = false;

      formData.value = {
        id: "",
        name: "",
        description: "",
        type: "permission",
        action: "use",
      };

      policyJSON.value = "";
      emit("refresh-assets");
      toast.add({
        severity: "success",
        summary: "Policy added",
        life: 3000,
      });
    } else {
      throw new Error(response.data.error || "Failed to save policy");
    }
  } catch (error) {
    console.log(error);

    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: error.response?.data?.detail || error,
      life: 5000,
    });
  }
};

const createPolicy = async () => {
  if (await isPcrHealthy()) {
    await openPCR();
    return;
  }

  openPolicyEditor();
};

const getPolicyId = (id) => {
  return id.split("/").pop() || id;
};

const getPolicyType = (type) => {
  return type?.split(":").pop() || type || "PolicyDefinition";
};

const getFormattedDate = (timestamp, full = false) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  if (full) {
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const truncateId = (id, length = 20) => {
  if (!id) return "N/A";
  if (id.length <= length) return id;
  return `${id.substring(0, length)}...`;
};

const getPermissionCount = (policy) => {
  return policy.policy?.odrl?.permission?.length || 0;
};

const getObligationCount = (policy) => {
  return policy.policy?.odrl?.obligation ? 1 : 0;
};

const getProhibitionCount = (policy) => {
  return policy.policy?.odrl?.prohibition?.length || 0;
};
const confirm2 = (event, policy) => {
  confirm.require({
    target: event.currentTarget,
    message: "Do you want to delete this record?",
    icon: "pi pi-info-circle",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: async () => {
      const selectedPolicy = visiblePolicies.value.find(
        (p) => p.key === policy.externalId
      );
      console.log("Visible policies: ", visiblePolicies.value);
      console.log("Selected policy: ", policy);

      console.log("selected policies are: ", selectedPolicy);

      try {
        if (selectedPolicy.connector) {
          const response = await axios.delete(
            `${API_BASE_URL}/provider/policy/${selectedPolicy.key}`
          );
          console.log("Response from provider API:", response.data);
          if (response.data.success) {
            await axios.delete(
              `${API_BASE_URL}/repository/delete/${selectedPolicy.store.id}`,
              {
                params: {
                  file_id: selectedPolicy.store.id, // required query param
                },
              }
            );
            toast.add({
              severity: "error",
              summary: "Policy Deleted",
              life: 5000,
            });
            emit("refresh-assets");
          } else {
            toast.add({
              severity: "error",
              summary: "Error Message",
              detail: response.data.error,
              life: 5000,
            });
          }
        } else {
          const response = await axios.delete(
            `${API_BASE_URL}/repository/delete/${selectedPolicy.store.id}`,
            {
              params: {
                file_id: selectedPolicy.store.id, // required query param
              },
            }
          );
          if (response.data.acknowledged) {
            toast.add({
              severity: "error",
              summary: "Policy Deleted",
              life: 5000,
            });
            emit("refresh-assets");
          }
        }
      } catch (error) {
        const errormsg = error.response?.data?.detail?.[0]?.message;
        console.error("Failed to delete policy:", errormsg);
        toast.add({
          severity: "error",
          summary: "Error Message",
          detail: errormsg,
          life: 5000,
        });
      }
    },
  });
};
watch(
  () => props.initialSource,
  (next) => {
    if (next && next !== sourceView.value) {
      sourceView.value = next;
    }
  }
);
</script>

<style scoped src="../assets/provider-view.css"></style>
